import React, {
  useCallback, useEffect, useMemo, useRef, useState
} from 'react';

import { getResourceTo } from 'react/utils/utils';
import { DefaultRequest } from 'scripts/api';

import SearchContacts from 'static/images/chat/search.svg';
import {
  getAllOrganizationsChildren,
  removeDuplicates,
  getOrganizationsIds,
  stringsMatchToSearch
} from './chatOrganizationsUtils';
import Loading from 'react/components/Loading';
import OrganizationListItem from './chatOrganizationItem';

function ChatOrganizationList() {
  const inputRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [organizations, setOrganizations] = useState(Monet.Session.Chat?.Organizations || []);
  const [selectedsOrganizations, setSelectedsOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  function enableOrganizationsAttendant(organizationsIds) {
    Monet.Session.Chat.chatApiCalls.enableAttendant(organizationsIds);
  }

  function disableOrganizationsAttendant(organizationIds) {
    Monet.Session.Chat.chatApiCalls.disableAttendant(organizationIds);
  }

  function handleSelectAllOrgs() {
    if (isAllOrganizationsSelected) {
      const organizationIds = selectedsOrganizations.map(({ id }) => id);

      disableOrganizationsAttendant(organizationIds);
      setSelectedsOrganizations([]);
    } else {
      const parsedOrganizations = organizations
        .map((item) => getAllOrganizationsChildren(item))
        .flat();

      const organizationIds = parsedOrganizations.map(({ id }) => id);
      const filteredOrgs = removeDuplicates(organizationIds);

      enableOrganizationsAttendant(filteredOrgs);
      setSelectedsOrganizations(parsedOrganizations);
    }
  }

  function selectParentAndChildrenOrganizations(organization) {
    const { organization: organizationItem } = organization;
    const parsedOrganizations = getAllOrganizationsChildren(organization);

    if (selectedsOrganizations.includes(organizationItem)) {
      const filteredOrgs = selectedsOrganizations.filter((org) => !parsedOrganizations.includes(org));

      const organizationIds = parsedOrganizations
        .filter((org) => selectedsOrganizations.includes(org))
        .map((org) => org.id);
      const organizationIdsFiltered = removeDuplicates(organizationIds);

      setSelectedsOrganizations(filteredOrgs);
      disableOrganizationsAttendant(organizationIdsFiltered);
    } else {
      const organizationIds = getOrganizationsIds(parsedOrganizations);
      const selectedOrganizationIds = getOrganizationsIds(selectedsOrganizations);

      const organizationIdsFiltered = removeDuplicates([...selectedOrganizationIds, ...organizationIds]);

      setSelectedsOrganizations([...selectedsOrganizations, ...parsedOrganizations]);
      enableOrganizationsAttendant(organizationIdsFiltered);
    }
  }

  function selectSingleOrganization(organization) {
    const updatedSelectedsOrganizations = [...selectedsOrganizations];
    if (updatedSelectedsOrganizations.includes(organization)) {
      updatedSelectedsOrganizations.splice(updatedSelectedsOrganizations.indexOf(organization), 1);
      disableOrganizationsAttendant([organization.id]);
    } else {
      const selectedOrganizationIds = getOrganizationsIds(selectedsOrganizations);
      const organizationIdsFiltered = removeDuplicates([...selectedOrganizationIds, organization.id]);

      updatedSelectedsOrganizations.push(organization);
      enableOrganizationsAttendant(organizationIdsFiltered);
    }

    setSelectedsOrganizations(updatedSelectedsOrganizations);
  }

  function handleSelectOrganization(item) {
    const { organization, childOrganizations } = item;

    if (childOrganizations) {
      selectParentAndChildrenOrganizations(item);
    } else {
      selectSingleOrganization(organization);
    }
  }

  function handleClickOrganization(organization) {
    if (selectedOrganization === organization) {
      setSelectedOrganization(null);
    } else {
      setSelectedOrganization(organization);
    }
  }

  function handleSearchOrgs() {
    const organizationsInSession = Monet.Session?.Chat?.Organizations || [];
    const { value } = inputRef.current;

    if (!value.length) {
      setOrganizations(organizationsInSession);
    }

    const findOrganizations = organizationsInSession
      .filter(({ organization }) => stringsMatchToSearch([organization.description, organization.key], value));

    setOrganizations(findOrganizations);
  }

  function setPresviousSelectedsOrganizationsAsMarked() {
    const storedOrganizations = Monet.Session?.Chat?.Organizations || organizations;

    if (!storedOrganizations.length) {
      return;
    }

    Monet.Session.Chat.chatApiCalls.attendantQueues((enabledOrgs) => {
      const allOrganizations = storedOrganizations
        .map((item) => getAllOrganizationsChildren(item))
        .flat();

      const organizationsPreviouslySelected = allOrganizations
        .filter((org) => enabledOrgs.some((enabledOrg) => enabledOrg.id === org.id));

      setSelectedsOrganizations(organizationsPreviouslySelected);
    });
  }

  function getAndListOrganizations() {
    setIsLoading(true);

    new DefaultRequest({
      method: 'GET',
      url: 'Organization/TreeHierarchy',
      success: (response) => {
        Monet.Session.Chat.Organizations = response;

        setOrganizations(response);
        setIsLoading(false);
        setPresviousSelectedsOrganizationsAsMarked();
      }
    }).send();
  }

  const isActive = useCallback(
    (organization) => selectedOrganization?.id === organization.id,
    [selectedOrganization]
  );

  const isAllOrganizationsSelected = useMemo(() => {
    const organizationsLength = organizations
      .map((item) => getAllOrganizationsChildren(item))
      .flat()
      .length;

    return selectedsOrganizations.length === organizationsLength;
  }, [organizations, selectedsOrganizations]);

  useEffect(() => {
    setPresviousSelectedsOrganizationsAsMarked();

    if (!Monet.Session?.Chat?.Organizations) {
      getAndListOrganizations();
    }
  }, []);

  return (
    <>
      {isLoading && <Loading height="150px" />}
      {!isLoading && (
        <div
          data-testid="organizations-list-body"
          className="organizations-list-body"
        >
          {organizations.length > 0 && (
            <div
              data-testid="select-all-button"
              className="select-all"
              onClick={handleSelectAllOrgs}
            >
              <input
                type="checkbox"
                checked={isAllOrganizationsSelected}
                readOnly
              />
              <span>{getResourceTo('Select all organizations')}</span>
            </div>
          )}
          {organizations.map((item, i) => (
            <OrganizationListItem
              key={i}
              selectedsOrganizations={selectedsOrganizations}
              organization={item}
              handleClickOrganization={handleClickOrganization}
              isActive={isActive}
              handleSelectOrganization={handleSelectOrganization}
            />
          ))}
        </div>
      )}
      <div className="organizations-list-footer">
        <div className="search-container">
          <input
            data-testid="search-input"
            ref={inputRef}
            type="text"
            placeholder={getResourceTo('suspenseActions.usersTab.searchContactsPlaceholder')}
          />
          <button
            data-testid="search-button"
            type="button"
            onClick={handleSearchOrgs}
          >
            <img src={SearchContacts} alt="Search Icon" />
          </button>
        </div>
      </div>
    </>
  );
}

export default ChatOrganizationList;
